// Inventory management types

export interface StockInfo {
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  current_stock: number
  reserved_stock: number
  available_stock: number
  minimum_stock: number
  is_low_stock: boolean
  last_updated: string
  location?: string
}

export interface StockAdjustment {
  id: string
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  adjustment_type: StockAdjustmentType
  old_stock: number
  new_stock: number
  adjustment: number
  reason: string
  reference_id?: string
  created_by: string
  created_at: string
}

export enum StockAdjustmentType {
  MANUAL = 'manual',
  SALE = 'sale',
  PURCHASE = 'purchase',
  RETURN = 'return',
  DAMAGE = 'damage',
  LOSS = 'loss',
  THEFT = 'theft',
  FOUND = 'found',
  CORRECTION = 'correction',
  TRANSFER = 'transfer',
  OTHER = 'other'
}

export interface LowStockVariant {
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  current_stock: number
  reserved_stock: number
  available_stock: number
  minimum_stock: number
  stock_deficit: number
  category_name?: string
}

export interface StockMovement {
  id: string
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  movement_type: string
  quantity: number
  reference_id?: string
  reason: string
  created_by: string
  created_at: string
}

export interface InventorySummary {
  total_variants: number
  total_stock_value: number
  low_stock_variants: number
  out_of_stock_variants: number
  total_reserved_stock: number
  categories_summary: CategoryStockSummary[]
}

export interface CategoryStockSummary {
  category_id: string
  category_name: string
  total_variants: number
  total_stock: number
  low_stock_count: number
  stock_value: number
}

export interface StockUpdateRequest {
  variant_id: string
  new_stock: number
  adjustment_type: StockAdjustmentType
  reason: string
  reference_id?: string
}

export interface StockAdjustmentRequest {
  variant_id: string
  adjustment: number
  adjustment_type: StockAdjustmentType
  reason: string
  reference_id?: string
}

export interface BulkStockUpdate {
  variant_id: string
  new_stock: number
  reason: string
}

export interface BulkStockUpdateResponse {
  successful_updates: number
  failed_updates: number
  errors: Array<{
    variant_id?: string
    sku?: string
    error: string
    row?: number
  }>
  total_processed: number
}

export interface StockImportResult {
  total_rows: number
  successful_imports: number
  failed_imports: number
  errors: Array<{
    row?: number
    sku?: string
    error: string
  }>
  import_id: string
  timestamp: string
}

export interface StockReservation {
  variant_id: string
  quantity: number
  order_id: string
}

export interface LowStockThresholdUpdate {
  minimum_stock: number
}

export interface StockAlert {
  id: string
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  alert_type: 'low_stock' | 'out_of_stock'
  current_stock: number
  minimum_stock: number
  created_at: string
  is_acknowledged: boolean
}

// Advanced Dashboard Types
export interface InventoryDashboardData {
  summary: InventoryDashboardSummary
  top_categories: TopCategoryBrand[]
  top_brands: TopCategoryBrand[]
  inventory_value_trend: InventoryValueTrend[]
  movement_velocity: MovementVelocityBreakdown
}

export interface InventoryDashboardSummary {
  total_variants: number
  total_stock_quantity: number
  total_stock_value: number
  low_stock_variants: number
  out_of_stock_variants: number
  overstocked_variants: number
  inventory_turnover_ratio: number
}

export interface TopCategoryBrand {
  category_name?: string
  brand_name?: string
  stock_value: number
  percentage: number
}

export interface InventoryValueTrend {
  month: string
  value: number
}

export interface MovementVelocityBreakdown {
  fast_moving: VelocityData
  medium_moving: VelocityData
  slow_moving: VelocityData
}

export interface VelocityData {
  count: number
  percentage: number
}

// Inventory Balancing Types
export interface BalancingRecommendation {
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  category_name?: string
  brand_name?: string
  warehouse_name?: string
  current_stock: number
  reserved_stock: number
  available_stock: number
  minimum_stock: number
  reorder_point: number
  average_daily_sales_30d: number
  average_daily_sales_60d: number
  average_daily_sales_90d: number
  days_of_stock_remaining: number
  suggested_order_quantity: number
  sales_velocity: 'fast' | 'medium' | 'slow'
  trend_direction: 'increasing' | 'stable' | 'decreasing'
  confidence_level: 'high' | 'medium' | 'low'
  last_restock_date?: string
  lead_time_days: number
  safety_stock_multiplier: number
}

export interface BalancingReportResponse {
  recommendations: BalancingRecommendation[]
  summary: {
    total_variants: number
    variants_needing_reorder: number
    total_suggested_order_value: number
    fast_moving_variants: number
    slow_moving_variants: number
  }
  filters_applied: {
    category_id?: string
    brand_id?: string
    warehouse_id?: string
    stock_status?: string
  }
  pagination: {
    limit: number
    offset: number
    total: number
    has_more: boolean
  }
}

export interface ReorderPointUpdate {
  variant_id: string
  new_reorder_point: number
  new_safety_stock_multiplier?: number
}

export interface BulkReorderPointUpdateRequest {
  updates: ReorderPointUpdate[]
}

export interface BulkReorderPointUpdateResponse {
  successful_updates: number
  failed_updates: number
  errors: Array<{
    variant_id: string
    error: string
  }>
  total_processed: number
}

export interface BalancingFilters {
  category_id?: string
  brand_id?: string
  warehouse_id?: string
  stock_status?: 'all' | 'low_stock' | 'out_of_stock' | 'overstocked' | 'needs_reorder'
  sales_velocity?: 'all' | 'fast' | 'medium' | 'slow'
  confidence_level?: 'all' | 'high' | 'medium' | 'low'
}

// Barcode Scanning Types
export interface ScannedItem {
  barcode: string
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  current_stock: number
  scanned_quantity: number
  timestamp: string
}

export interface BarcodeVariantLookup {
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  current_stock: number
  reserved_stock: number
  available_stock: number
  minimum_stock: number
  barcode: string
  barcode_type: string
  category_name?: string
  brand_name?: string
  warehouse_name?: string
}

export interface BatchStockAdjustmentRequest {
  adjustments: Array<{
    variant_id: string
    adjustment_type: StockAdjustmentType
    quantity: number
    reason: string
  }>
}

export interface BatchStockAdjustmentResponse {
  successful_adjustments: number
  failed_adjustments: number
  errors: Array<{
    variant_id: string
    variant_sku: string
    error: string
  }>
  total_processed: number
}

export interface BarcodeScanError {
  barcode: string
  error: string
  timestamp: string
}

// Advanced Stock Adjustment Types
export interface StockAdjustmentDetail {
  id: string
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  warehouse_id?: string
  warehouse_name?: string
  adjustment_type: StockAdjustmentType
  quantity: number
  reason: string
  evidence_urls: string[]
  unit_cost?: number
  total_cost_impact?: number
  status: 'pending' | 'approved' | 'rejected'
  created_by_admin_id: string
  created_by_admin_name: string
  approved_by_admin_id?: string
  approved_by_admin_name?: string
  approved_at?: string
  created_at: string
  updated_at: string
}

export interface StockAdjustmentCreate {
  variant_id: string
  warehouse_id?: string
  adjustment_type: StockAdjustmentType
  quantity: number
  reason: string
  evidence_urls?: string[]
}

export interface ShrinkageReport {
  period_start: string
  period_end: string
  total_inventory_value: number
  total_shrinkage_value: number
  shrinkage_percentage: number
  breakdown: {
    damage: { count: number; value: number; percentage: number }
    loss: { count: number; value: number; percentage: number }
    theft: { count: number; value: number; percentage: number }
  }
  top_affected_categories: Array<{
    category_name: string
    shrinkage_value: number
    shrinkage_percentage: number
  }>
}

export interface StockAdjustmentFilters {
  adjustment_type?: StockAdjustmentType | 'all'
  status?: 'all' | 'pending' | 'approved' | 'rejected'
  warehouse_id?: string
  category_id?: string
  brand_id?: string
  start_date?: string
  end_date?: string
  created_by_admin_id?: string
}

// Alert Management Types
export interface LowStockAlert {
  id: string
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  warehouse_id?: string
  warehouse_name?: string
  alert_level: 'critical' | 'high' | 'medium' | 'low'
  current_stock: number
  minimum_stock: number
  reorder_point: number
  suggested_order_quantity: number
  days_until_stockout?: number
  status: 'active' | 'acknowledged' | 'resolved'
  acknowledged_by_admin_id?: string
  acknowledged_by_admin_name?: string
  acknowledged_at?: string
  resolved_at?: string
  notification_sent: boolean
  notification_sent_at?: string
  created_at: string
  updated_at: string
}

export interface AlertSummary {
  total_alerts: number
  active_alerts: number
  acknowledged_alerts: number
  resolved_alerts: number
  critical_alerts: number
  high_alerts: number
  medium_alerts: number
  low_alerts: number
}

export interface AlertFilters {
  status?: 'all' | 'active' | 'acknowledged' | 'resolved'
  alert_level?: 'all' | 'critical' | 'high' | 'medium' | 'low'
  warehouse_id?: string
  category_id?: string
  brand_id?: string
  start_date?: string
  end_date?: string
}

export interface AlertAcknowledge {
  alert_id: string
  admin_id: string
}

export interface AlertThresholdConfig {
  critical_threshold: number // percentage of minimum stock (e.g., 0 = out of stock)
  high_threshold: number     // percentage of minimum stock (e.g., 50 = 50% of minimum)
  medium_threshold: number   // percentage of minimum stock (e.g., 75 = 75% of minimum)
  low_threshold: number      // percentage of minimum stock (e.g., 100 = at minimum)
}

// Barcode Management Types
export interface BarcodeGenerateRequest {
  variant_id: string
  barcode_type?: 'EAN13' | 'CODE128' | 'QR'
}

export interface BarcodeGenerateResponse {
  variant_id: string
  barcode: string
  barcode_type: string
  success: boolean
  message?: string
}

export interface BulkBarcodeGenerateRequest {
  variant_ids: string[]
  barcode_type?: 'EAN13' | 'CODE128' | 'QR'
}

export interface BulkBarcodeGenerateResponse {
  successful_generations: number
  failed_generations: number
  results: Array<{
    variant_id: string
    barcode?: string
    error?: string
  }>
  total_processed: number
}

export interface BarcodeLabelRequest {
  variant_ids: string[]
  label_size: '40x25' | '50x30' | '70x40'
  quantities?: { [variant_id: string]: number }
}

// Inventory Report Types
export interface StockHistoryReportItem {
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  category_name?: string
  brand_name?: string
  warehouse_name?: string
  date: string
  movement_type: string
  quantity: number
  stock_before: number
  stock_after: number
  reference_id?: string
  reference_type?: string
  unit_cost?: number
  total_cost?: number
  created_by_admin_name?: string
}

export interface StockHistoryReportResponse {
  items: StockHistoryReportItem[]
  summary: {
    total_movements: number
    opening_stock: number
    closing_stock: number
    net_change: number
    total_purchases: number
    total_sales: number
    total_adjustments: number
  }
  filters_applied: StockHistoryFilters
  pagination: {
    limit: number
    offset: number
    total: number
    has_more: boolean
  }
}

export interface StockHistoryFilters {
  start_date?: string
  end_date?: string
  variant_id?: string
  category_id?: string
  brand_id?: string
  warehouse_id?: string
  movement_type?: string
}

export interface SalesPatternReportItem {
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  category_name?: string
  brand_name?: string
  total_units_sold_30d: number
  total_units_sold_60d: number
  total_units_sold_90d: number
  average_weekly_sales: number
  sales_velocity: 'fast' | 'medium' | 'slow'
  trend_direction: 'increasing' | 'stable' | 'decreasing'
  trend_percentage: number
  current_stock: number
  days_of_stock_remaining: number
  is_overstocked: boolean
  confidence_level: 'high' | 'medium' | 'low'
}

export interface SalesPatternReportResponse {
  items: SalesPatternReportItem[]
  summary: {
    total_variants: number
    fast_moving_count: number
    medium_moving_count: number
    slow_moving_count: number
    overstocked_count: number
    top_performers: Array<{
      variant_sku: string
      product_name: string
      total_sales: number
      revenue: number
    }>
  }
  filters_applied: SalesPatternFilters
  pagination: {
    limit: number
    offset: number
    total: number
    has_more: boolean
  }
}

export interface SalesPatternFilters {
  category_id?: string
  brand_id?: string
  warehouse_id?: string
  sales_velocity?: 'all' | 'fast' | 'medium' | 'slow'
  trend_direction?: 'all' | 'increasing' | 'stable' | 'decreasing'
  confidence_level?: 'all' | 'high' | 'medium' | 'low'
  overstocked_only?: boolean
}

export interface StockValuationReportItem {
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  category_name?: string
  brand_name?: string
  warehouse_name?: string
  current_stock: number
  unit_cost: number
  unit_retail_price: number
  total_cost_value: number
  total_retail_value: number
  potential_profit: number
  profit_margin_percentage: number
  abc_classification: 'A' | 'B' | 'C'
  percentage_of_total_value: number
}

export interface StockValuationReportResponse {
  items: StockValuationReportItem[]
  summary: {
    total_variants: number
    total_cost_value: number
    total_retail_value: number
    total_potential_profit: number
    average_profit_margin: number
    abc_analysis: {
      a_class: { count: number; value: number; percentage: number }
      b_class: { count: number; value: number; percentage: number }
      c_class: { count: number; value: number; percentage: number }
    }
  }
  filters_applied: StockValuationFilters
  pagination: {
    limit: number
    offset: number
    total: number
    has_more: boolean
  }
}

export interface StockValuationFilters {
  warehouse_id?: string
  category_id?: string
  brand_id?: string
  abc_classification?: 'all' | 'A' | 'B' | 'C'
  valuation_date?: string
}

export interface StockAgingReportItem {
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  category_name?: string
  brand_name?: string
  warehouse_name?: string
  current_stock: number
  unit_cost: number
  total_value: number
  average_age_days: number
  age_0_30_days: { quantity: number; value: number }
  age_31_60_days: { quantity: number; value: number }
  age_61_90_days: { quantity: number; value: number }
  age_91_180_days: { quantity: number; value: number }
  age_over_180_days: { quantity: number; value: number }
  is_stale_inventory: boolean
  requires_attention: boolean
}

export interface StockAgingReportResponse {
  items: StockAgingReportItem[]
  summary: {
    total_variants: number
    total_stock_value: number
    age_breakdown: {
      age_0_30_days: { quantity: number; value: number; percentage: number }
      age_31_60_days: { quantity: number; value: number; percentage: number }
      age_61_90_days: { quantity: number; value: number; percentage: number }
      age_91_180_days: { quantity: number; value: number; percentage: number }
      age_over_180_days: { quantity: number; value: number; percentage: number }
    }
    stale_inventory_count: number
    attention_required_count: number
  }
  filters_applied: StockAgingFilters
  pagination: {
    limit: number
    offset: number
    total: number
    has_more: boolean
  }
}

export interface StockAgingFilters {
  warehouse_id?: string
  category_id?: string
  brand_id?: string
  age_bracket?: 'all' | '0-30' | '31-60' | '61-90' | '91-180' | '180+'
  stale_only?: boolean
  attention_required_only?: boolean
}